import type { AppScope, AuthorizationRole } from './authorizationModel'

export type AuthorizationSource = 'auth0' | 'url-jwt' | 'canvas'

export interface AuthorizationState {
  source: AuthorizationSource | null
  partnerId: string | null
  partnerUserId: string | null
  roles: AuthorizationRole[]
  scopes: AppScope[]
  policyLoadStatus: 'idle' | 'loading' | 'ready' | 'error'
  policyLoadError: string | null
  claimsLoaded: boolean
  isAuthenticated: boolean
}

export enum AuthorizationActionTypes {
  HYDRATE_AUTHORIZATION = 'HYDRATE_AUTHORIZATION',
  CLEAR_AUTHORIZATION = 'CLEAR_AUTHORIZATION',
  SET_POLICY_LOAD_STATE = 'SET_POLICY_LOAD_STATE',
}

type HydrateAuthorizationPayload = Partial<
  Omit<AuthorizationState, 'claimsLoaded' | 'isAuthenticated'>
> & {
  claimsLoaded?: boolean
  isAuthenticated?: boolean
}

type AuthorizationAction =
  | {
      type: AuthorizationActionTypes.HYDRATE_AUTHORIZATION
      payload: HydrateAuthorizationPayload
    }
  | {
      type: AuthorizationActionTypes.SET_POLICY_LOAD_STATE
      payload: {
        policyLoadStatus: AuthorizationState['policyLoadStatus']
        policyLoadError: string | null
      }
    }
  | {
      type: AuthorizationActionTypes.CLEAR_AUTHORIZATION
    }

export const initialState: AuthorizationState = {
  source: null,
  partnerId: null,
  partnerUserId: null,
  roles: [],
  scopes: [],
  policyLoadStatus: 'idle',
  policyLoadError: null,
  claimsLoaded: false,
  isAuthenticated: false,
}

export const AuthorizationReducer = (
  state = initialState,
  action: AuthorizationAction
): AuthorizationState => {
  switch (action.type) {
    case AuthorizationActionTypes.HYDRATE_AUTHORIZATION: {
      return {
        ...state,
        ...action.payload,
        claimsLoaded: action.payload.claimsLoaded ?? true,
        isAuthenticated: action.payload.isAuthenticated ?? true,
      }
    }
    case AuthorizationActionTypes.SET_POLICY_LOAD_STATE:
      return {
        ...state,
        policyLoadStatus: action.payload.policyLoadStatus,
        policyLoadError: action.payload.policyLoadError,
      }
    case AuthorizationActionTypes.CLEAR_AUTHORIZATION:
      return {
        ...initialState,
        policyLoadStatus: state.policyLoadStatus,
        policyLoadError: state.policyLoadError,
      }
    default:
      return state
  }
}
